import { defineComponent } from "vue"
import Vector from "@/components/icons/Vector.vue"
import ToggleInput from "@/components/uikit/inputs/ToggleInput.vue"
import Button from "@/components/uikit/Button.vue"
import { callApi } from "@/services/api"
import { TokenData } from "@/store/types"
import DangerTriangle from "@/components/icons/DangerTriangle.vue"
import SmallCopyIcon from "@/components/icons/SmallCopyIcon.vue"
import { useLoginState } from "@/components/login/composables/useLoginState"
import TermsModalWithButton from "@/components/login/TermsModalWithButton.vue"
import { AxiosError } from "axios"
import { ApiResponseError } from "@/services/types"

const { state } = useLoginState()

enum ActiveComponents {
  StartAuthorize = "StartAuthorize",
  Authorize = "Authorize",
}
export const INITIAL_FIELDS = {
  username: "",
}
export const INITIAL_ERRORS = {
  username: [],
  phrase: [],
}
export default defineComponent({
  components: {
    Vector,
    ToggleInput,
    Button,
    DangerTriangle,
    SmallCopyIcon,
    TermsModalWithButton,
  },
  name: "LoginModal",
  emits: ["close", "changeModalContent"],
  data() {
    return { state, authorizing: false }
  },
  methods: {
    closeModal() {
      this.$emit("close")
    },
    async getReferralByCode(referralCode: string) {
      const response = await callApi.getReferralByCode(referralCode)
      if (response.data.status) {
        this.state.referral = response.data.data
      }
    },
    async copyDescription() {
      const el = this.$refs.referralLink as HTMLElement
      if (el) {
        try {
          await navigator.clipboard.writeText(el.innerText)
          this.state.copied = true
          setTimeout(() => {
            this.state.copied = false
          }, 2000)
        } catch (err) {
          console.error("Copy error", err)
        }
      }
    },
    changeContent(
      component: keyof typeof ActiveComponents,
      payload?: { description?: string; username?: string }
    ) {
      this.state.activeComponent = component

      if (payload) {
        this.state.description = payload.description || ""
        this.state.fields.username = payload.username || ""
      }
    },
    async getPhrase() {
      try {
        this.authorizing = true
        this.state.errors.username = []
        const response = await callApi.getPhrase(
          this.state.fields.username,
          this.state.terms_and_policy
        )
        this.changeContent("Authorize", {
          description: response.data.data.suggested_new_description,
          username: this.state.fields.username,
        })
      } catch (error: unknown) {
        const err = error as AxiosError<ApiResponseError>
        if (
          err.response?.data.messages.username &&
          err.response.data.messages.username.length > 0
        ) {
          this.state.errors.username = err.response.data.messages.username
        }

        if (err.status === 429) {
          this.state.errors.phrase = [
            "Too many requests. Please wait a moment and try again.",
          ]
        }

        console.error("Error:", error)
      } finally {
        this.authorizing = false
      }
    },
    async authorize() {
      try {
        this.authorizing = true
        this.state.errors.phrase = []
        const response = await callApi.authorize(
          this.state.fields.username,
          this.state.referral?.id
        )

        if (response.status) {
          const {
            data: {
              data: { access_token, refresh_token },
            },
          } = response

          const tokenData: TokenData = {
            access_token,
            refresh_token,
          }
          await this.$store.dispatch("auth/setTokenData", tokenData)
          Object.keys(response.data.data).forEach((key) => {
            localStorage.setItem(key, response.data.data[key])
          })

          await this.$store.dispatch("auth/setUser", response.data.data.user)

          await this.$store.dispatch(
            "auth/handleRedirectAfterLogin",
            this.$router
          )
          window.location.reload()
        }
      } catch (error: unknown) {
        const err = error as AxiosError<ApiResponseError>
        const phrase = err.response?.data.messages?.phrase
        if (phrase && phrase.length > 0) {
          this.state.errors.phrase = phrase
        }

        if (err.status === 429) {
          this.state.errors.phrase = [
            "Too many requests. Please wait a moment and try again.",
          ]
        }
        console.error("Error:", err)
      } finally {
        this.authorizing = false
      }
    },
  },
})
